import { useQuery } from "@tanstack/react-query";
import type { FetchWindow } from "../../timeline/geometry";
import { usePlayer } from "../../state/player";
import { api, qs } from "../client";
import { REALTIME_CLIENT_ID } from "../../realtime/protocol";
import { nativeRuntime } from "../../platform/native";

import type { Bounds, NowPlaying, TimelineWindow, Waveform } from "../types";

export function useBounds() {
  return useQuery({
    queryKey: ["bounds"],
    queryFn: () => api.get<Bounds>("/api/timeline/bounds"),
  });
}

export function useNowPlaying() {
  const playing = usePlayer((s) => s.playing);
  return useQuery({
    queryKey: ["nowplaying"],
    queryFn: () =>
      api.get<NowPlaying>(
        `/api/nowplaying${playing && nativeRuntime() === null ? qs({ c: REALTIME_CLIENT_ID }) : ""}`,
      ),
  });
}

export function useTimeline(startMs: number, endMs: number, width: number, _live = false) {
  const start = new Date(startMs).toISOString();
  const end = new Date(endMs).toISOString();
  return useQuery({
    queryKey: ["timeline", start, end, width],
    queryFn: ({ signal }) =>
      api.get<TimelineWindow>(`/api/timeline${qs({ start, end, width })}`, signal),
    // Width is zero until the canvas container has been measured, and a zero width collapses
    // the fetch window to a single instant -- a request the server refuses twice over, on the
    // width bound and on the empty range. The bound here is the server's own, so the client
    // never asks for a window that could only come back as a 422.
    enabled: width >= 64,
    // The window the caller asks for is quantised (see timeline/geometry), so panning
    // reuses a cached window until it crosses a quantum boundary. Keeping the previous
    // result visible during that fetch is what stops the canvas flashing empty on every
    // pan.
    placeholderData: (previous) => previous,
    staleTime: 30_000,
  });
}

/** Widest window a waveform is fetched for; mirrors MAX_WAVEFORM_WINDOW in api/timeline.py. */
export const WAVEFORM_MAX_SPAN_MS = 3 * 86_400_000;

export function useWaveform(window_: FetchWindow | null, _live = false) {
  // Zero width when there is no window, which is what holds the query off: `enabled` alone
  // would still hand back the previous response through `placeholderData`, and a stale
  // envelope placed by its own span draws as a bar stranded mid-screen.
  const width = window_?.width ?? 0;
  const start = window_ ? new Date(window_.startMs).toISOString() : "";
  const end = window_ ? new Date(window_.endMs).toISOString() : "";
  return useQuery({
    queryKey: ["waveform", start, end, width],
    queryFn: ({ signal }) =>
      api.get<Waveform>(`/api/timeline/waveform${qs({ start, end, width })}`, signal),
    enabled: width >= 64,
    // Safe only because the renderer places columns from the response's own start/end, never
    // from the current view: stale peaks then cover the range they actually describe and
    // slide off screen instead of stretching over the new one.
    placeholderData: (previous) => previous,
    staleTime: 30_000,
  });
}
